import { useEffect, useSyncExternalStore } from 'react'
import { flushSync } from 'react-dom'
import { businessLinesApi, type MyBusinessLines } from '../api/businessLines'

/**
 * ADR-019 (Equal Point): il ramo in cui si sta lavorando. 'generale' è la vista con i totali di tutti i rami.
 * Il ramo scelto si ricorda (localStorage `ecrm_branch`) e vale per tutta l'app; i rami dell'utente arrivano
 * una volta per sessione da `GET /api/business-lines/mine` (cache di modulo, come le feature flag).
 */
export const GENERAL = 'generale'
const KEY = 'ecrm_branch'

const listeners = new Set<() => void>()
let stored: string | null = read()
let mine: MyBusinessLines | null = null
let loading: Promise<void> | null = null
let generation = 0

function read(): string | null {
  try { return localStorage.getItem(KEY) } catch { return null }
}

function emit(): void { listeners.forEach(l => l()) }

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

/**
 * Il ramo da mostrare: quello salvato se l'utente può ancora aprirlo, altrimenti la Generale se la vede,
 * altrimenti il primo dei suoi rami. Null se l'utente non ha rami (Equal Point non si mostra).
 */
export function effectiveBranch(my: MyBusinessLines | null, saved: string | null): string | null {
  if (!my) return null
  if (saved === GENERAL && my.general) return GENERAL
  if (saved && my.lines.some(l => l.slug === saved)) return saved
  if (my.general) return GENERAL
  return my.lines[0]?.slug ?? null
}

/** Cambia ramo; `animate` false per i cambi automatici (nessuna transizione, nessun flushSync fuori da un evento). */
export function setBranch(slug: string, { animate = true }: { animate?: boolean } = {}): void {
  if (slug === stored) return
  if (!animate) {
    stored = slug
    try { localStorage.setItem(KEY, slug) } catch { /* modalità privata: si resta in memoria */ }
    emit()
    return
  }
  const commit = () => {
    stored = slug
    try { localStorage.setItem(KEY, slug) } catch { /* modalità privata: si resta in memoria */ }
    // Sincrono: la transizione fotografa la pagina nuova (menu e colori del ramo) appena finisce questa funzione.
    flushSync(emit)
  }
  animateBranchChange(commit)
}

type TransitionDocument = Document & { startViewTransition?: (update: () => void) => { finished: Promise<void> } }

/**
 * Cambio di ramo animato: la pagina col colore del nuovo ramo si apre a cerchio dal selettore del ramo, mentre quella
 * vecchia arretra sotto (View Transitions, stili in layout.css). Senza supporto del browser o con il movimento ridotto
 * il cambio è immediato.
 */
function animateBranchChange(commit: () => void): void {
  const doc = document as TransitionDocument
  let reduce = false
  try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches } catch { /* ambiente senza matchMedia */ }
  if (typeof doc.startViewTransition !== 'function' || reduce) { commit(); return }
  const chip = doc.querySelector('.md-branch-chip')?.getBoundingClientRect()
  const x = chip ? chip.left + chip.width / 2 : window.innerWidth - 80
  const y = chip ? chip.top + chip.height / 2 : 32
  const root = doc.documentElement
  root.style.setProperty('--branch-x', `${x}px`)
  root.style.setProperty('--branch-y', `${y}px`)
  // Cerchio e anello arrivano insieme all'angolo più lontano (l'anello parte da 20px di raggio).
  const radius = Math.ceil(Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y)))
  root.style.setProperty('--branch-r', `${radius}px`)
  root.style.setProperty('--branch-ring-scale', String(Math.ceil(radius / 20)))
  root.classList.add('branch-switching')
  // Solo nello stato nuovo, col colore del nuovo ramo: la transizione lo anima come un'onda dal selettore.
  const ring = doc.createElement('div')
  ring.className = 'branch-ring'
  ring.setAttribute('aria-hidden', 'true')
  ring.style.left = `${x}px`
  ring.style.top = `${y}px`
  doc.startViewTransition(() => { commit(); doc.body.appendChild(ring) }).finished.finally(() => {
    ring.remove()
    root.classList.remove('branch-switching')
  })
}

/** Da chiamare al logout: il prossimo utente ricarica i suoi rami. */
export function resetBranchCache(): void {
  generation++
  mine = null
  loading = null
  emit()
}

function load(): void {
  if (loading) return
  // Una risposta arrivata dopo il logout non deve dare al prossimo utente i rami del precedente.
  const gen = generation
  loading = businessLinesApi.mine()
    .then(r => { if (gen === generation) mine = r })
    .catch(() => { if (gen === generation) mine = { general: true, lines: [] } })
    .finally(emit)
}

/** I rami dell'utente e il ramo corrente; `enabled` false (flag spento) non chiama il server. */
export function useBranch(enabled: boolean): { mine: MyBusinessLines | null; branch: string | null } {
  useEffect(() => { if (enabled) load() }, [enabled])
  const snapshot = useSyncExternalStore(subscribe, () => mine, () => null)
  const saved = useSyncExternalStore(subscribe, () => stored, () => null)
  if (!enabled) return { mine: null, branch: null }
  return { mine: snapshot, branch: effectiveBranch(snapshot, saved) }
}

// Altre schede: il ramo cambiato altrove vale anche qui.
try {
  window.addEventListener('storage', e => { if (e.key === KEY || e.key === null) { stored = read(); emit() } })
} catch { /* ambiente senza window (test node) */ }
